'use client';

import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import { ChevronDown } from 'lucide-react';
import Link from 'next/link';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';

type Tab = {
  id: number;
  label: string;
  href: string;
  Icon: LucideIcon;
};

type FinanceLink = { name: string; href: string };

const CLOSE_DELAY_MS = 180; // 👈 “чуть отошёл” и не закрылось сразу

export function FinanceHoverMenu({
  tab,
  financeLinks,
  pathname,
  active,
}: {
  tab: Tab;
  financeLinks: FinanceLink[];
  pathname: string;
  active: boolean;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  const [left, setLeft] = useState<number>(0);
  const [open, setOpen] = useState(false);

  const closeTimerRef = useRef<number | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  // вычисляем смещение меню так, чтобы оно начиналось ровно под кнопкой
  const recalc = useCallback(() => {
    const wrap = wrapRef.current;
    const btn = btnRef.current;
    if (!wrap || !btn) return;

    const wrapRect = wrap.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    setLeft(btnRect.left - wrapRect.left);
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    recalc();
    setOpen(true);
  }, [clearCloseTimer, recalc]);

  const closeMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
  }, [clearCloseTimer]);

  const scheduleClose = useCallback(
    (delay = CLOSE_DELAY_MS) => {
      clearCloseTimer();
      closeTimerRef.current = window.setTimeout(() => setOpen(false), delay);
    },
    [clearCloseTimer],
  );

  useLayoutEffect(() => {
    recalc();
    const onResize = () => recalc();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [recalc]);

  // закрывать при смене маршрута (и для клика по текущему /finance тоже — см. onClick ниже)
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // закрывать при клике вне меню
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      if (!wrap.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  // чтобы при табе/фокусе внутри не закрывалось, а при уходе фокуса наружу — закрывалось
  const onBlurCapture = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      const next = e.relatedTarget as Node | null;
      if (next && wrapRef.current?.contains(next)) return;
      scheduleClose(0);
    },
    [scheduleClose],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
        btnRef.current?.focus();
      }
    },
    [closeMenu],
  );

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={() => scheduleClose()}
      onFocusCapture={openMenu}
      onBlurCapture={onBlurCapture}
      onKeyDown={onKeyDown}
    >
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => {
            const next = !v;
            if (next) recalc();
            return next;
          });
        }}
        className={clsx(
          'flex items-center gap-1 px-3 py-1.5 rounded-xl hover:bg-white/20 dark:hover:bg-slate-500/50',
          active && 'bg-white/30 dark:text-sky-400 font-semibold',
        )}
      >
        <tab.Icon size={18} />
        <span>{tab.label}</span>
        <ChevronDown className={cn('ml-0.5 h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>

      <div
        role="menu"
        style={{ left }}
        className={cn(
          'absolute top-full mt-2 w-[260px] rounded-xl border bg-white p-2 shadow-lg z-50',
          'dark:border-slate-700 dark:bg-slate-900',
          'transition duration-150',
          open
            ? 'opacity-100 visible translate-y-0 pointer-events-auto'
            : 'opacity-0 invisible translate-y-1 pointer-events-none',
        )}
      >
        {/* “мостик” на 8px над меню — чтобы не рвалось при маленьком зазоре */}
        <div className="absolute -top-2 left-0 right-0 h-2 bg-transparent" />

        <ul className="grid gap-1">
          {financeLinks.map((link) => {
            const isSubActive = pathname === link.href;

            return (
              <li key={link.href}>
                <Link
                  role="menuitem"
                  href={link.href}
                  prefetch={false}
                  aria-current={isSubActive ? 'page' : undefined}
                  onClick={() => {
                    // ✅ закрываем даже если это текущий роут
                    closeMenu();
                    // на всякий: убираем фокус, чтобы не держалось focus-within
                    btnRef.current?.blur();
                  }}
                  className={cn(
                    'block rounded-lg px-3 py-2 text-sm transition-colors',
                    'text-slate-900 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800',
                    isSubActive &&
                      'bg-blue-100 font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
                  )}
                >
                  {link.name}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
